import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { importInvestorEntitiesQuickFilterOptions } from '../../../constants';
import { IHouseholdComparison, TypeWindow } from '../../../types';
import { columns } from '../constants';
import MobileTable from './mobileTable';
import TableBody from './tableBody';

interface TableProps {
  tableData: IHouseholdComparison[];
  openInvestorImportDetailPage: (type: TypeWindow, row: IHouseholdComparison) => void;
  sort: ISort;
  setSort: Dispatch<SetStateAction<ISort>>;
  search: string;
  onChangeSearch: (value: string) => void;
  primaryFilter: string;
  changePrimaryFilterValue: (filter: string) => void;
}

const Table = ({
  tableData,
  openInvestorImportDetailPage,
  sort,
  setSort,
  search,
  onChangeSearch,
  primaryFilter,
  changePrimaryFilterValue
}: TableProps) => {
  const { isMobile } = useResponsive();

  const [sortedTableData, setSortedTableData] = useState<IHouseholdComparison[]>([]);

  useEffect(() => {
    const sortedData = [...tableData].sort((a, b) => {
      switch (sort.key) {
        case 'HOUSEHOLD':
          return sort.asc ? a.householdName.localeCompare(b.householdName) : b.householdName.localeCompare(a.householdName);
        case 'TOTAL_ENTITIES':
          return sort.asc ? a.totalEntities - b.totalEntities : b.totalEntities - a.totalEntities;
        case 'ENTITIES_ADDED':
          return sort.asc ? a.entitiesAdded - b.entitiesAdded : b.entitiesAdded - a.entitiesAdded;
        case 'ENTITIES_REMOVED':
          return sort.asc ? a.entitiesRemoved - b.entitiesRemoved : b.entitiesRemoved - a.entitiesRemoved;
        case 'ADVISOR_OWNER':
          return sort.asc ? a.advisorEmail.localeCompare(b.advisorEmail) : b.advisorEmail.localeCompare(a.advisorEmail);
        case 'STATUS':
          return sort.asc ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
        default:
          return 0;
      }
    });

    setSortedTableData(sortedData);
  }, [sort, tableData]);

  const filteredTableData = () => {
    const newTableData = [...sortedTableData];
    const searchLower = search.toLowerCase();
    const filteredTableData = newTableData.filter(
      (item) => (item.householdName ?? '').toLowerCase().includes(searchLower) || (item.advisorEmail ?? '').toLowerCase().includes(searchLower)
    );

    switch (primaryFilter) {
      case 'Updates Only':
        return filteredTableData.filter((item) => item.status !== 'error' && item.status !== 'unchanged');
      case 'Errors Only':
        return filteredTableData.filter((item) => item.status === 'error');
      default:
        return filteredTableData;
    }
  };

  return (
    <>
      <FilterPage
        search={{
          value: search,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        primaryFilter={{
          value: primaryFilter,
          onChange: changePrimaryFilterValue,
          selects: ['All Rows', 'Updates Only', 'Errors Only']
        }}
        quickFilter={{
          title: 'Show',
          value: primaryFilter,
          onChange: changePrimaryFilterValue,
          defaultValue: 'All Rows',
          options: importInvestorEntitiesQuickFilterOptions
        }}
        resultsValue={filteredTableData().length}
        useNewFilters
      />
      <TableWrapper padding="0">
        {isMobile ? (
          <MobileTable tableData={filteredTableData()} openInvestorImportDetailPage={openInvestorImportDetailPage} />
        ) : (
          <>
            <TableHeader refetch={() => null as any} columns={columns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              <TableBody tableData={filteredTableData()} openInvestorImportDetailPage={openInvestorImportDetailPage} />
            </TableBodyWrapper>
          </>
        )}
      </TableWrapper>
    </>
  );
};

export default Table;
